/**
 * PlaylistBannerCard — sponsor banner rendered as the FIRST card in the
 * branded playlist tray (side-rail + bottom-playlist layouts).
 *
 * Driven by `sponsor.slots.playlistBanner`. Two modes, inferred from the
 * data (no schema flag) exactly like <TopBannerSlot>:
 *
 *   - Image-only: BOTH `title` and `ctaLabel` are blank → the uploaded
 *     `assetUrl` is the entire card (a complete pre-composed banner) and
 *     the whole card is the click target.
 *   - Rich: icon tile + headline/subtitle + CTA pill.
 *
 * Unlike the top banner this is a STATIC list item — no slide-in timer,
 * no dismiss. It still counts as real sold inventory: one ad-impression
 * per clip play (via useImpressionBeacon) and an ad-click on tap (via
 * fireAdClickBeacon), both with slot="playlistBanner".
 *
 * Card chrome follows Figma (HLTV-Allstar-iFrame-v3.0, nodes 111:1774
 * desktop / 114:680 mobile): a frosted-black chip — rgba(0,0,0,0.75)
 * over a 2px backdrop blur, no border — so it reads as its own card
 * distinct from the blue now-playing clip beside it. The brand color is
 * carried by the logo + CTA pill, not the card fill. Width is capped
 * (CARD_MAX_WIDTH) so it stays a compact chip on the wide desktop
 * canvases; the mobile horizontal strip uses a fixed-width carousel card.
 */
import { useRecoilValue } from "recoil";
import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";

import {
  BrandedLayoutVariantState,
  BrandedSponsorConfigState,
  BrandedViewerGeoState,
} from "../../State";
import { fireAdClickBeacon, useImpressionBeacon } from "../../tracking";

/* A touch shorter than <PlaylistRow>'s 60px ROW_HEIGHT — the lead
   sponsor chip reads as a compact header above the clip rows rather than
   matching their full height. */
const CARD_MIN_HEIGHT = 36;

/* Desktop width caps so the banner reads as a compact chip instead of
   stretching the full rail / iframe width. The card stretches (align-self)
   to fill its column up to these caps on the wider side-rail +
   bottom-playlist canvases. */
const CARD_MAX_WIDTH = 300;
const CARD_MAX_WIDTH_BOTTOM = 340;

/**
 * Rich-mode card shell. Per Figma (HLTV-Allstar-iFrame-v3.0, nodes
 * 111:1774 desktop / 114:680 mobile) the lead slot is a frosted-black
 * chip — rgba(0,0,0,0.75) over a 2px backdrop blur, NO border, 8px
 * radius — so it reads as its own card distinct from the blue
 * now-playing clip beside it. Brand color is carried by the logo + CTA
 * pill, not the card fill. The card stretches (align-self) to fill its
 * column up to CARD_MAX_WIDTH so it reads as a compact chip on the
 * desktop side-rail / bottom-playlist canvases (never the full rail /
 * iframe width); the mobile horizontal strip uses a fixed 225px
 * carousel card, centered in the row.
 */
const CardShell = styled.a<{ $bottomPlaylist?: boolean }>`
  align-items: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  border-radius: 8px;
  /* Include padding in the element's width so width:auto + the
     2px margin-right never push the card past its parent's edge. */
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  /* Tight 8px icon→text gap so the headline keeps its width budget; the
     CopyBlock flex-grows so the CTA pill is pushed to the right edge
     (space-between) when there's room. */
  gap: 8px;
  margin-right: 2px;
  min-height: ${CARD_MIN_HEIGHT}px;
  padding: 4px 8px;
  text-decoration: none;
  /* align-self: stretch fills the cross axis of whatever flex track the
     card sits in — width on the desktop column (capped by max-width so
     it stays a compact chip, never the full rail), and HEIGHT on the
     mobile horizontal row (so the chip matches the clip cards' height
     instead of collapsing to bare content). The 2px margin-right is
     subtracted from the stretched size, so it never overflows the
     parent. Mobile sets a fixed main-axis width below. */
  align-self: stretch;
  max-width: ${CARD_MAX_WIDTH}px;
  transition: background 120ms ease;

  &:hover {
    background: rgba(0, 0, 0, 0.6);
  }

  /* Bottom-playlist: same frosted-black chip, just a touch more
     horizontal padding for the wider canvas. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    margin-right: 0;
    padding: 4px 10px;
    max-width: ${CARD_MAX_WIDTH_BOTTOM}px;
  `}

  /* Mobile horizontal carousel: fixed-width card. 225px gives the
     headline + CTA pill enough room while still reading as a
     peek-the-next-card carousel item beside the 220px clip rows.
     align-self: center keeps the chip its own compact height (not
     stretched to the taller clip cards) and vertically centered in the
     row; the explicit min-height stops it collapsing to a tiny strip. */
  @media (max-width: 560px) {
    flex: 0 0 225px;
    align-self: center;
    margin-right: 0;
    min-height: 46px;
    padding: 4px 8px;
    width: 225px;
    max-width: none;
  }
`;

/**
 * Image-only card shell. Raw — no border/tint/radius chrome — because
 * the uploaded asset IS the creative; any decoration alters what the
 * sponsor approved in the campaign editor. Same width rules as the rich
 * shell so it slots into the tray identically.
 */
const ImageOnlyCard = styled.a<{ $bottomPlaylist?: boolean }>`
  display: block;
  flex: 0 0 auto;
  box-sizing: border-box;
  margin-right: 2px;
  overflow: hidden;
  text-decoration: none;
  /* Match the rich shell: align-self: stretch fills the desktop column
     width (capped by max-width), with the 2px margin subtracted so it
     never overflows the parent. Mobile overrides with a fixed carousel
     width and centers (rather than stretches) so the image keeps its
     aspect ratio. */
  align-self: stretch;
  max-width: ${CARD_MAX_WIDTH}px;

  & > img {
    border-radius: 8px;
    display: block;
    height: auto;
    width: 100%;
  }

  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    margin-right: 0;
    max-width: ${CARD_MAX_WIDTH_BOTTOM}px;
  `}

  @media (max-width: 560px) {
    flex: 0 0 225px;
    align-self: center;
    margin-right: 0;
    width: 225px;
    max-width: none;
  }
`;

/** Square brand-asset tile on the left of the rich card. object-fit:
 *  contain so non-square raster uploads letterbox instead of stretching;
 *  SVGs letterbox natively. */
const IconTile = styled(Box)<{ $bottomPlaylist?: boolean }>`
  flex: 0 0 auto;
  height: 22px;
  width: 22px;

  & img {
    height: 100%;
    object-fit: contain;
    width: 100%;
  }

  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    height: 24px;
    width: 24px;
  `}
`;

const CopyBlock = styled(Flex)`
  flex: 1 1 auto;
  flex-direction: column;
  gap: 1px;
  justify-content: center;
  min-width: 0;
`;

const TitleText = styled(Text)<{ $bottomPlaylist?: boolean }>`
  color: #ffffff;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0px;
  line-height: 14px;
  /* Single line, never wraps. Type + gaps are sized down so real
     campaign headlines ("Collect FREE cases") fit one line at the
     compact card widths; anything longer than the budget ellipsizes
     rather than wrapping the chip to a second row. */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    font-size: 12px;
    line-height: 15px;
  `}
`;

const SubtitleText = styled(Text)<{ $bottomPlaylist?: boolean }>`
  /* Subtitle (URL) a notch dimmer than the bold white headline, sized
     down to keep the chip compact and single-line. */
  color: rgba(255, 255, 255, 0.65);
  font-size: 9px;
  font-weight: 500;
  line-height: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    font-size: 10px;
    line-height: 13px;
  `}
`;

/** CTA pill — brand-colored, mirrors the top banner's CTA recipe. */
const CtaPill = styled.span<{
  $bg: string;
  $color: string;
  $bottomPlaylist?: boolean;
}>`
  align-items: center;
  background: ${({ $bg }) => $bg};
  border-radius: 999px;
  color: ${({ $color }) => $color};
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 9px;
  font-weight: 700;
  height: 20px;
  letter-spacing: 0px;
  padding: 0 9px;
  white-space: nowrap;

  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    font-size: 10px;
    height: 22px;
    padding: 0 11px;
  `}
`;

export const PlaylistBannerCard = () => {
  const sponsor = useRecoilValue(BrandedSponsorConfigState);
  const layoutVariant = useRecoilValue(BrandedLayoutVariantState);
  const isBottomPlaylist = layoutVariant === "bottomPlaylist";
  const viewerGeo = useRecoilValue(BrandedViewerGeoState);

  const banner = sponsor?.slots.playlistBanner;

  // Impression fires once the card dwells in view (≥50% for ≥500ms); the
  // dedup key includes the active clip so it re-arms per clip play, same
  // as the other sold slots.
  const impressionRef = useImpressionBeacon({
    slot: "playlistBanner",
    creativeId: banner?.id,
    enabled: !!banner,
  });

  const handleClick = () => {
    if (!banner) return;
    fireAdClickBeacon({
      campaignId: sponsor?.campaignId,
      slot: "playlistBanner",
      creativeId: banner.id,
      viewerGeo,
    });
  };

  if (!banner) return null;

  // Image-only mode: campaign uploaded a complete pre-composed creative
  // and left BOTH title and ctaLabel blank — the asset is the whole card.
  const hasTitle = !!banner.title && banner.title.trim().length > 0;
  const hasCtaLabel = !!banner.ctaLabel && banner.ctaLabel.trim().length > 0;
  const isImageOnly = !!banner.assetUrl && !hasTitle && !hasCtaLabel;

  if (isImageOnly) {
    return (
      <ImageOnlyCard
        href={banner.ctaUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={banner.altText}
        $bottomPlaylist={isBottomPlaylist}
        ref={impressionRef}
        onClick={handleClick}
      >
        <img src={banner.assetUrl} alt={banner.altText} />
      </ImageOnlyCard>
    );
  }

  const ctaBg = banner.ctaBgColor ?? sponsor.theme?.primary ?? "#FF2864";
  const ctaColor = banner.ctaTextColor ?? "#fff";

  return (
    <CardShell
      href={banner.ctaUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={banner.altText || banner.title}
      $bottomPlaylist={isBottomPlaylist}
      ref={impressionRef}
      onClick={handleClick}
    >
      {banner.assetUrl && (
        <IconTile $bottomPlaylist={isBottomPlaylist}>
          <img src={banner.assetUrl} alt={banner.altText} />
        </IconTile>
      )}
      <CopyBlock>
        {hasTitle && (
          <TitleText $bottomPlaylist={isBottomPlaylist}>
            {banner.title}
          </TitleText>
        )}
        {banner.subtitle && (
          <SubtitleText $bottomPlaylist={isBottomPlaylist}>
            {banner.subtitle}
          </SubtitleText>
        )}
      </CopyBlock>
      {hasCtaLabel && (
        <CtaPill
          $bg={ctaBg}
          $color={ctaColor}
          $bottomPlaylist={isBottomPlaylist}
        >
          {banner.ctaLabel}
        </CtaPill>
      )}
    </CardShell>
  );
};
